import { DEFAULT_PLAYER_RATIO } from '../constants/editorConst';
import { timeStampToMs } from './TimeUtil';
import { BASE_VU_WIDTH } from '../../common/constants/common';
import { v4 as uuidv4 } from 'uuid';
import VideoService from '../service/VideoService';
import { CARD_MIDDLE_PADDING_PERCENTAGE, CARD_PADDING_PERCENTAGE, POSITIONS } from '../constants/callToActions';
import { PANEL_TYPE } from 'engage/routes/CompanySettings/components/TextPanels/constants';
import { checkIsFullyTransparent } from '../utils/colorHelper';

/**
 * Vormats geometry https://vormats.atlassian.net/wiki/spaces/VORMATS/pages/607518721/Vormats+Geometry
 *
 * All measurements for logo are introduced as Vormats virtual unit (vu).
 * Base width is 320 pixels.
 * To get the value in px
 * (video width px / 320 px) * 1 vu = 6 px.
 *
 */

export const VIDEO_ORIENTATIONS = {
  landscape: 'landscape',
  square: 'square',
  portrait: 'portrait',
};

const BASE_TEX_PANEL_DURATION = 5000;
const TEXT_PANEL_FADEOUT_DURATION = 500;
const TEXT_PANEL_PNG_COEFF = 6;
const BORDER_RADIUS_COEFF = 4;

const CTA_MARGIN_PERCENTAGE = 0.05;
const CTA_PADDING_PERCENTAGE = 0.01;
const CTA_SHADOW_MARGIN = 20;
const CTA_LINE_HEIGHT = 1.2;
const CTA_LINE_HEIGHT_Y_OFFSET_MULTIPLIER = 6;
const CTA_MULTILINE_LINE_HEIGHT = 1.5;
const CTA_Y_LINE_HEIGHT_OFFSET_MULTIPLIER = 1.5; // 1.5 is a magic number to align the text in the middle of the container as the web canvas has a different text rendering than the canvas in the app

/**
 * String that will help to calculate the max hight for the font.
 * Canvas API measureText() method measuring only current text do not taking into account
 * letters in upper case if those is not in the string. Also it's do not includes the
 * letters with different height
 */
const HEIGHT_TEST_STRING = 'LOREM ipsum dolor sit amet';

export const getPlayerMixedOrientation = scenes => {
  const orientationsArray = Array.from(new Set(scenes.map(({ orientation }) => orientation)));
  if (orientationsArray?.length > 1) {
    if (orientationsArray.includes('landscape')) {
      return VIDEO_ORIENTATIONS.landscape;
    } else {
      return VIDEO_ORIENTATIONS.square;
    }
  }
  return null;
};

export const calculateVideoOrientation = scenes => {
  const notProcessingScenes = scenes.filter(scene => !scene.processing);
  const orientationsArray = Array.from(new Set(notProcessingScenes.map(({ orientation }) => orientation)));
  if (orientationsArray?.length > 1) {
    if (orientationsArray.includes('landscape')) {
      return VIDEO_ORIENTATIONS.landscape;
    } else if (orientationsArray.includes('portrait')) {
      return VIDEO_ORIENTATIONS.portrait;
    } else {
      return VIDEO_ORIENTATIONS.square;
    }
  } else {
    return VIDEO_ORIENTATIONS.landscape;
  }
};

export const getMaxDimensions = scenes => {
  let maxDimensions = {};

  const scenesDimensions = scenes?.map(({ dimensions }) => dimensions);

  if (scenesDimensions && scenesDimensions?.find(item => item?.width === null)) {
    return { width: DEFAULT_PLAYER_RATIO };
  }

  const scenesToCheckWith = scenes.filter(item => item.hasOwnProperty('dimensions'));

  const widestSceneRatio = Math.max(...scenesToCheckWith?.map(({ dimensions }) => dimensions.width / dimensions.height));
  const highestSceneRatio = Math.min(...scenesToCheckWith?.map(({ dimensions }) => dimensions.width / dimensions.height));

  // Add a constraint for either the height or width
  // Both restrictions cannot exist at the same time,
  // because one of them must be filled in in the player
  if (widestSceneRatio < DEFAULT_PLAYER_RATIO) {
    maxDimensions.width = widestSceneRatio;
  } else if (highestSceneRatio > DEFAULT_PLAYER_RATIO) {
    maxDimensions.height = highestSceneRatio;
  } else {
    maxDimensions.width = DEFAULT_PLAYER_RATIO;
  }

  return maxDimensions;
};

export const generateTextPanelsArray = (sortedScenes, introDuration = 0, editorMode = false) => {
  if (!sortedScenes.some(item => item.hasOwnProperty('nameCard'))) {
    return [];
  }
  let sceneShift = introDuration;
  const textPanels = sortedScenes.map((item, index, array) => {
    if (index > 0) {
      const trimEnd =
        (typeof array[index].trimEnd === 'string' ? timeStampToMs(array[index - 1].trimEnd) : array[index - 1].trimEnd) /
        (array[index - 1].playbackRate || 1);
      const trimStart =
        (typeof array[index].trimStart === 'string' ? timeStampToMs(array[index - 1].trimStart) : array[index - 1].trimStart) /
        (array[index - 1].playbackRate || 1);
      sceneShift += trimEnd - trimStart;
    }
    const trimEnd = timeStampToMs(item.trimEnd) / (item.playbackRate || 1);
    const trimStart = timeStampToMs(item.trimStart) / (item.playbackRate || 1);
    const sceneDuration = trimEnd - trimStart;
    let start = sceneShift;
    if (editorMode) {
      start += trimStart;
    }
    let end = start + BASE_TEX_PANEL_DURATION;
    if (sceneDuration < BASE_TEX_PANEL_DURATION) {
      end = sceneShift + sceneDuration - TEXT_PANEL_FADEOUT_DURATION;
      if (editorMode) {
        end += start;
      }
    }
    return { start, end, text: item.nameCard ? item.nameCard : null, id: item.id };
  });
  return textPanels;
};

export const generateTransitionsArray = (sortedScenes, transitions, introDuration = 0, editorMode = false) => {
  const scenes = sortedScenes.map(item => ({
    ...item,
    trimStart: timeStampToMs(item.trimStart),
    trimEnd: timeStampToMs(item.trimEnd),
  }));
  const generatedTransitions = transitions.map(transition => {
    const leftScene = transition.leftMediaId ? scenes.find(item => item.id === transition.leftMediaId) : null;
    const rightScene = transition.rightMediaId ? scenes.find(item => item.id === transition.rightMediaId) : null;

    let customTransitionRatio = null;

    const isSomeSceneIsLandscape = scenes.some(({ orientation }) => orientation === 'landscape');

    if (!isSomeSceneIsLandscape) {
      const scenesRatio = scenes.map(({ dimensions }) => dimensions.height / dimensions.width);
      const minSceneRation = Math.min(...scenesRatio);

      customTransitionRatio = minSceneRation;
    }

    let start = introDuration;

    if (leftScene) {
      const leftSceneIndex = scenes.indexOf(leftScene);
      start -= transition.durationMs / 2;

      if (leftSceneIndex > 0) {
        start += scenes
          .slice(0, leftSceneIndex + 1)
          .map(item => (item.trimEnd - item.trimStart) / (item?.playbackRate || 1))
          .reduce((accum, value) => accum + value);
      } else {
        const duration = scenes[leftSceneIndex].trimEnd - scenes[leftSceneIndex].trimStart;
        start += duration / (scenes[leftSceneIndex]?.playbackRate || 1);
      }
    } else {
      if (rightScene) {
        const rightSceneIndex = scenes.indexOf(rightScene);

        if (rightSceneIndex > 0) {
          start += scenes
            .slice(0, rightSceneIndex + 1)
            .map(item => (item.trimEnd - item.trimStart) / (item?.playbackRate || 1))
            .reduce((accum, value) => accum + value);
        } else {
          const duration = scenes[rightSceneIndex].trimEnd - scenes[rightSceneIndex].trimStart;
          start += duration / (scenes[rightSceneIndex]?.playbackRate || 1);
        }
      }
    }

    const end = start + transition.durationMs;

    return { start, end, type: transition.type, durationMs: transition.durationMs, customTransitionRatio, leftScene, rightScene };
  });

  return generatedTransitions;
};

const parsePadding = paddingString => {
  const result = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };
  const splittedPadding = paddingString?.split(' ');
  let i = 0;
  for (let key in result) {
    result[key] = Number(splittedPadding[i]) * TEXT_PANEL_PNG_COEFF;
    i++;
  }
  return result;
};

const parseLabelStyle = (labelObject, type) => {
  if (labelObject) {
    const labelBorderWidth = labelObject.borderWidth;
    const fontSize = labelObject.fontSize * TEXT_PANEL_PNG_COEFF;
    let padding = "";
    let backgroundColor = "";
    if (type === 1) {
      padding = parsePadding(labelObject.textPadding);
      backgroundColor = labelObject.backgroundColor;
    }
    return {
      borderWidth: labelBorderWidth * TEXT_PANEL_PNG_COEFF,
      borderColor: labelObject?.borderColor,
      borderRadius: labelObject?.borderRadius * BORDER_RADIUS_COEFF,
      opacity: labelObject.opacity,
      color: labelObject.fontColor,
      fontFamily: `'${labelObject.fontName ?? labelObject?.font?.name}'`,
      fontSize,
      padding,
      backgroundColor,
    };
  }
};

function drawBg(ctx, x, y, width, height, style) {
  ctx.save();
  /// add border
  if (style.borderWidth > 0) {
    ctx.fillStyle = style.borderColor;
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, style.borderRadius);
    ctx.fill();
  }

  /// color for background
  ctx.fillStyle = style.backgroundColor;
  /// draw background rect assuming height of font
  ctx.beginPath();
  ctx.roundRect(
    x + style.borderWidth / 2,
    y + style.borderWidth / 2,
    width - style.borderWidth,
    height - style.borderWidth,
    style.borderRadius * 0.75
  );
  ctx.fill();
  ctx.restore();
}

function drawTextBG(ctx, text, x, y, style, background = false, textDimensions) {
  // Save current state as we make a lot of changes
  ctx.save();
  // Draw text from top - makes life easier at the moment
  ctx.textBaseline = 'top';
  if (background) {
    drawBg(ctx, x, y, textDimensions.width, textDimensions.height, style);
  }
  ctx.fillStyle = style.color;
  ctx.font = `${style.fontSize}px ${style.fontFamily}`;
  const textPosition = { top: 0, left: 0 };
  if (background) {
    textPosition.top = style.padding?.top + style.borderWidth;
    textPosition.left = style.padding?.left + style.borderWidth;
  }
  // Draw text on top
  ctx.fillText(text, x + textPosition.left, y + textPosition.top);
  // Restore original state
  ctx.restore();
}

export const generateTextPanelPng = async (template, topText, bottomText, callback) => {
  const type = template?.type;
  const onePanelType = type === PANEL_TYPE.ONE_PANEL.type;
  let spacing = template.spacing * TEXT_PANEL_PNG_COEFF;
  const containerStyles = {};
  // Get labels styles objects
  const topLabel = onePanelType ? template.labelTop : template.panelTop;
  const bottomLabel = onePanelType ? template.labelBottom : template.panelBottom;

  const topLabelStyles = parseLabelStyle(topLabel, type);
  const bottomLabelStyles = parseLabelStyle(bottomLabel, type);
  // If the type on ONE_PANEL get styles for a container form label_top
  if (onePanelType) {
    containerStyles.padding = parsePadding(template.textPadding);
    containerStyles.backgroundColor = template.bgColor;
    containerStyles.borderWidth = topLabelStyles.borderWidth;
    containerStyles.borderRadius = topLabelStyles.borderRadius;
    containerStyles.borderColor = topLabelStyles.borderColor;
  }
  // Create a virtual canvas to create a text panel image
  const canvas = document.createElement('canvas');
  var ctx = canvas.getContext('2d');
  ctx.globalAlpha = bottomLabelStyles.opacity;

  const getFontStringWithQuotes = fontFamily => {
    return fontFamily.startsWith("'") || fontFamily.startsWith('"') ? fontFamily : `'${fontFamily}'`;
  };

  try {
    const topFontString = `${topLabelStyles.fontSize}px ${getFontStringWithQuotes(topLabelStyles.fontFamily)}`;
    const topPanelFontExists = document.fonts.check(topFontString);
    if (!topPanelFontExists) {
      const topLabelFont = new FontFace(topLabelStyles.font.name, `url(${topLabelStyles?.font?.url})`);
      const topFont = await topLabelFont.load();
      document.fonts.add(topFont);
    }
    const differentFonts = topLabelStyles.fontFamily !== bottomLabelStyles.fontFamily;
    if (differentFonts) {
      const bottomFontString = `${bottomLabelStyles.fontSize}px ${getFontStringWithQuotes(bottomLabelStyles.fontFamily)}`;
      const bottomPanelFontExists = document.fonts.check(bottomFontString);
      if (!bottomPanelFontExists) {
        const bottomLabelFont = new FontFace(bottomLabelStyles.font.name, `url(${bottomLabelStyles?.font?.url})`);
        const bottomFont = await bottomLabelFont.load();
        document.fonts.add(bottomFont);
      }
    }
  } catch (e) {
    console.log('Will apply default font. Error while custom font loading:', e);
  }

  try {
    // Get Text panel dimensions
    const topTextDimensions = calculateTextDimensions(ctx, topText, onePanelType, topLabelStyles);
    const bottomTextDimensions = calculateTextDimensions(ctx, bottomText, onePanelType, bottomLabelStyles);
    // Calculate canvas size
    let width = Math.max(topTextDimensions.width, bottomTextDimensions.width);
    let height = topTextDimensions.height + bottomTextDimensions.height + spacing;
    if (onePanelType) {
      // If one padel - add padding values to calculation
      width += containerStyles.padding.left + containerStyles.padding.right;
      height += containerStyles.padding.top + containerStyles.padding.bottom;
    }
    canvas.width = `${Math.round(width)}`;
    canvas.height = `${Math.round(height)}`;
    if (onePanelType) {
      // If panel type is ONE_PANEL draw a background
      drawBg(ctx, 0, 0, width, height, containerStyles);
    }
    const textPosition = { top: 0, left: 0 };
    if (onePanelType) {
      textPosition.top = containerStyles.padding.top;
      textPosition.left = containerStyles.padding.left;
    }
    // Draw top text if it exists
    topText?.length && drawTextBG(ctx, topText, textPosition.left, textPosition.top, topLabelStyles, !onePanelType, topTextDimensions);
    textPosition.top = topTextDimensions.height + spacing;
    if (onePanelType) {
      textPosition.top += containerStyles.padding.top;
    }
    // Draw bottom text if it exists
    bottomText?.length &&
      drawTextBG(ctx, bottomText, textPosition.left, textPosition.top, bottomLabelStyles, !onePanelType, bottomTextDimensions);
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    await callback(dataUrl);
  } catch (e) {
    console.log('Error while generating an image', e);
    return false;
  }
  return true;
};

const calculateTextDimensions = (ctx, text, isOnePanel, textStyles) => {
  let width = 0;
  let height = 0;
  if (text?.length > 0) {
    ctx.font = `${textStyles.fontSize}px ${textStyles.fontFamily}`;
    const textMetrics = ctx.measureText(text);
    width = textMetrics.width;
    const maxHeightMetrics = ctx.measureText(HEIGHT_TEST_STRING);
    height = maxHeightMetrics.actualBoundingBoxAscent + maxHeightMetrics.actualBoundingBoxDescent;
    if (!isOnePanel) {
      width += textStyles.padding.left + textStyles.padding.right + textStyles.borderWidth * 2;
      height += textStyles.padding.top + textStyles.padding.bottom + textStyles.borderWidth * 2;
    }
  }
  return { width, height };
};

// CTA Generation

const recalculateVuToPx = (vu, smallestSide) => {
  return (vu * smallestSide) / BASE_VU_WIDTH;
};

/**
 * @param appConfig
 * @param videoDimensions
 * @param callAction
 * @returns {Promise<string>}
 */
export const generateCallActionPng = async (
  appConfig,
  videoDimensions,
  callAction
) => {
  const fontSize = recalculateVuToPx(
    Math.max(15, callAction?.size),
    Math.min(videoDimensions.width, videoDimensions.height)
  );
  const borderRadius = recalculateVuToPx(
    callAction?.radius,
    Math.min(videoDimensions.width, videoDimensions.height)
  );


  const isLandscape = videoDimensions.width > videoDimensions.height;

  const isMiddle = callAction?.position.includes('middle');

  const positionSizeLimiter = isMiddle && isLandscape ? 0.5 : 1;

  const ctaLabelStyle = {
    color: callAction.textColor,
    fontFamily: `'${callAction.font.name}'`,
    fontSize: fontSize,
    ...(callAction.fullscreen
      ? { textAlign: callAction.position.split("_")[1] ?? "center" }
      : {}),
  };

  let maxDrawWidth =
    videoDimensions.width *
    positionSizeLimiter *
    (1 - (isMiddle ? 0 : CTA_MARGIN_PERCENTAGE * 2)) -
    borderRadius;

  if (callAction.fullscreen) {
    if (callAction.position === 'middle') {
      maxDrawWidth =
        videoDimensions.width * (1 - (CARD_MIDDLE_PADDING_PERCENTAGE * 2));
    } else {
      maxDrawWidth = videoDimensions.width * (1 - (CARD_PADDING_PERCENTAGE) * 2);
    }
  }

  const containerStyles = {
    backgroundColor: callAction.bgColor,
    borderColor: callAction.bgColor,
    borderRadius: borderRadius,
    maxDrawWidth,
  };

  // Create a virtual canvas to create a call action image
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Load font for cta
  try {
    const ctaFontExists = document.fonts.check(
      `${ctaLabelStyle.fontSize}px ${ctaLabelStyle.fontFamily}`
    );

    if (!ctaFontExists) {
      const topFontFamily = appConfig.fonts.find(
        (item) => item.name === ctaLabelStyle.fontFamily.replaceAll('\'', '')
      );
      const topLabelFont = new FontFace(
        topFontFamily.name,
        `url(${topFontFamily.font_url})`
      );
      const topFont = await topLabelFont.load();
      document.fonts.add(topFont);
    }
  } catch (e) {
    console.log(
      'Will apply default font. Error while custom font loading:',
      e
    );
    throw e;
  }

  try {
    const { lines, containerWidth, containerHeight } = calculateTextIntoLines(
      ctx,
      callAction.text,
      containerStyles,
      ctaLabelStyle
    );

    canvas.width = callAction.fullscreen
      ? videoDimensions.width
      : Math.round(
        containerWidth +
        CTA_SHADOW_MARGIN +
        borderRadius +
        containerWidth * CTA_PADDING_PERCENTAGE * 2
      );
    canvas.height = callAction.fullscreen
      ? videoDimensions.height
      : Math.round(containerHeight + CTA_SHADOW_MARGIN);

    const drawableContainerWidth = callAction.fullscreen ? videoDimensions.width :
      containerWidth +
      containerWidth * CTA_PADDING_PERCENTAGE * 2 +
      borderRadius;

    drawCTABg(
      ctx,
      callAction.fullscreen ? 0 : CTA_SHADOW_MARGIN / 2,
      0,
      drawableContainerWidth,
      callAction.fullscreen ? videoDimensions.height : containerHeight,
      containerStyles
    );
    drawCTAText(
      ctx,
      lines,
      ctaLabelStyle,
      callAction.fullscreen ? videoDimensions.width : drawableContainerWidth,
      callAction.fullscreen ? videoDimensions.height : null,
      callAction.fullscreen
    );

    return canvas.toDataURL('image/png', 1.0);
  } catch (e) {
    console.log('Error while generating an image', e);
    throw e;
  }
};

function drawCTABg(ctx, x, y, width, height, style) {
  ctx.save();

  // Only draw background if color is valid (hex, not 'none', not 'transparent')
  if (
    style.borderColor &&
    style.borderColor !== 'none' &&
    style.borderColor !== 'transparent' &&
    !checkIsFullyTransparent(style.borderColor)
  ) {
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 8;
    ctx.shadowBlur = 16;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.fillStyle = style.borderColor;
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, style.borderRadius);
    ctx.fill();
  }

  ctx.restore();
}

function splitLongWord(ctx, word, containerStyles) {
  let parts = [];
  let part = '';

  for (let char of word) {
    let tempPart = part + char;
    let metrics = ctx.measureText(tempPart);
    let textWidth = metrics.width;

    if (textWidth > containerStyles.maxDrawWidth) {
      if (part !== '') {
        parts.push(part);
        part = char;
      }
    } else {
      part = tempPart;
    }
  }

  if (part !== '') {
    parts.push(part);
  }

  return parts;
}

function calculateTextIntoLines(
  ctx,
  text,
  containerStyles,
  ctaLabelStyle,
) {
  const newLines = text.split('\n');
  let line = '';
  let lines = [];
  let containerWidth = 0;
  let containerHeight = 0;
  ctx.font = `${ctaLabelStyle.fontSize}px ${ctaLabelStyle.fontFamily}`;

  for (let i = 0; i < newLines.length; i++) {
    const words = newLines[i].split(' ');
    for (let j = 0; j < words.length; j++) {
      let word = words[j];
      let wordWidth = ctx.measureText(word).width;

      if (wordWidth > containerStyles.maxDrawWidth) {
        if (line !== '') {
          lines.push(line.trim());
        }
        let wordParts = splitLongWord(ctx, word, containerStyles);
        for (let j = 0; j < wordParts.length; j++) {
          const splitWord = wordParts[j];
          let tempLineWidth = ctx.measureText(splitWord).width;
          lines.push(splitWord.trim());
          containerWidth = Math.max(containerWidth, tempLineWidth);
        }
      } else {
        let tempLine = line + word + ' ';
        let tempLineWidth = ctx.measureText(tempLine).width;

        if (tempLineWidth > containerStyles.maxDrawWidth) {
          if (line !== '') {
            lines.push(line.trim());
          }
          tempLineWidth = ctx.measureText(line).width;
          line = word + ' '; // Add the word to the new line
        } else {
          line = tempLine;
        }

        containerWidth = Math.max(containerWidth, tempLineWidth);

        if (j === words.length - 1 && line !== "") {
          lines.push(line.trim());
        }
      }
    }
    line = '';
  }

  if (lines.length > 1 && newLines.left === 1) {
    containerWidth = containerStyles.maxDrawWidth;
  }

  const lineHeight =
    ctaLabelStyle.fontSize *
    (lines.length > 1 ? CTA_MULTILINE_LINE_HEIGHT : CTA_LINE_HEIGHT);
  containerHeight =
    lines.length * lineHeight +
    (lines.length == 1
      ? lineHeight / CTA_LINE_HEIGHT_Y_OFFSET_MULTIPLIER
      : 0);

  return {
    lines,
    containerWidth,
    containerHeight,
  };
}

function drawCTAText(
  ctx,
  lines,
  fontStyle,
  containerWidth,
  containerHeigh = null,
  fullscreen = false
) {
  let x = containerWidth / 2 + CTA_SHADOW_MARGIN / 2;

  if (fullscreen) {
    switch (fontStyle.textAlign) {
      case 'left':
        x =
          containerWidth *
          (CTA_MARGIN_PERCENTAGE / 2 + CTA_PADDING_PERCENTAGE * 2);
        break;
      case 'right':
        x =
          containerWidth -
          containerWidth *
          (CTA_MARGIN_PERCENTAGE / 2 + CTA_PADDING_PERCENTAGE);
        break;
      default:
        break;
    }
  }

  let lineHeight =
    fontStyle.fontSize *
    (lines.length > 1 ? CTA_MULTILINE_LINE_HEIGHT : CTA_LINE_HEIGHT); // Adjust line height as needed

  let y =
    (lineHeight * CTA_Y_LINE_HEIGHT_OFFSET_MULTIPLIER) /
    CTA_LINE_HEIGHT_Y_OFFSET_MULTIPLIER;
  if (fullscreen) {
    y = (containerHeigh - lineHeight * lines.length) / 2;
  }

  ctx.save();
  ctx.textBaseline = 'top';
  ctx.textAlign = fontStyle.textAlign ?? 'center';
  ctx.fillStyle = fontStyle.color;
  ctx.font = `${fontStyle.fontSize}px ${fontStyle.fontFamily}`;

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], x, y);
    y += lineHeight;
  }

  ctx.restore();
}

export const checkFontLoaded = async font => {
  if (font?.font_url || font?.url) {
    const fontFaceSet = await document.fonts.ready;
    const fontFaces = [...fontFaceSet];
    const isFontLoaded = fontFaces.find(item => item.family === font.name) !== undefined;
    if (!isFontLoaded) {
      var labelFont = new FontFace(font.name, `url(${font.font_url || font.url})`);
      const loadedFace = await labelFont.load();
      document.fonts.add(loadedFace);
    }
  }
};

export async function uploadScenesTextPanelsPng(videoId, scenes) {
  const textPanels = scenes.map(({ id, nameCard }) => ({
    uploadId: uuidv4(),
    sceneId: id,
    ...nameCard,
  }));
  const failedSceneIds = [];
  try {
    const presignedUrlBody = textPanels.map(({ uploadId, sceneId }) => ({
      uploadId,
      sceneId,
      resourceType: 'textpanel',
    }));
    // Request presigned Urls from backend
    const presignedUrls = await VideoService.getUploadUrl(videoId, { urlRequests: presignedUrlBody });
    await Promise.allSettled(
      textPanels.map(async item => {
        const uploadObject = presignedUrls.find(urlObject => item.uploadId === urlObject.uploadId);
        if (uploadObject) {
          const uploadPng = async imgBlobUrl => {
            const imgFile = await fetch(imgBlobUrl).then(res => res.arrayBuffer());
            if (imgFile.byteLength) {
              const result = await VideoService.uploadPngByUrl(uploadObject.url, uploadObject.uploadId, imgFile);
              try {
                const { size } = await VideoService.checkFileInS3(uploadObject.url);
                if (size === 0) {
                  failedSceneIds.push(item.sceneId);
                  return;
                }
              } catch (e) {
                failedSceneIds.push(item.sceneId);
                return;
              }
              // If return not null push failed url to array
              result && failedSceneIds.push(item.sceneId);
            } else {
              failedSceneIds.push(item.sceneId);
            }
          };
          // Use presigned url to generate and upload text panel png
          const result = await generateTextPanelPng(item.template, item.topText, item.bottomText, uploadPng);
          if (!result) {
            failedSceneIds.push(item.sceneId);
          }
        }
      })
    );
  } catch (e) {
    console.log('Error uploading text panels', e);
  }
  return failedSceneIds;
}
